import { Fragment, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import PageHeader from '../components/PageHeader.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';

// Task #116 fix (P1-4, claude/p0-p1-readiness-review-2026-08-30-status.md): this page used to
// fetch a single flat `limit: 200` batch from the server and paginate over it client-side --
// once the audit_log table passed 200 rows, everything older than the newest 200 was permanently
// unreachable no matter how many "Next" clicks an operator made. Now mirrors AuctionLogPage.jsx's
// real server-side keyset pagination (`before`/`nextCursor`) against the fixed GET /api/audit-log
// route, plus new `from`/`to` date-range inputs the old flat-limit version had no way to support
// (there was nothing to filter server-side against -- the whole page was one static batch).
export default function AuditLogPage() {
  const [rows, setRows] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [entityType, setEntityType] = useState('');
  const [actor, setActor] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const result = await api.auditLog({
        entityType: entityType || undefined,
        actor: actor || undefined,
        from: from || undefined,
        to: to || undefined,
        limit: 50,
      });
      setRows(result.rows);
      setNextCursor(result.nextCursor);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the audit log.');
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const result = await api.auditLog({
        entityType: entityType || undefined,
        actor: actor || undefined,
        from: from || undefined,
        to: to || undefined,
        limit: 50,
        before: nextCursor,
      });
      setRows((prev) => [...(prev || []), ...result.rows]);
      setNextCursor(result.nextCursor);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load more.');
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page">
      <PageHeader title="Audit Log" subtitle="Every admin write across the exchange, for compliance and debugging." />

      <div className="toolbar">
        <input
          className="toolbar__search"
          placeholder="Filter by entity type (e.g. publisher, demand_partner)&hellip;"
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
        />
        <input
          className="toolbar__search"
          placeholder="Filter by actor&hellip;"
          value={actor}
          onChange={(e) => setActor(e.target.value)}
        />
        <label className="toolbar__search" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ border: 'none', flex: 1 }} />
        </label>
        <label className="toolbar__search" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ border: 'none', flex: 1 }} />
        </label>
        <button className="btn btn--ghost" onClick={load}>
          Apply filters
        </button>
      </div>

      {loading && <LoadingState label="Loading audit log" />}
      {!loading && error && <EmptyState title="Couldn't load audit log" message={error} />}
      {!loading && !error && rows && rows.length === 0 && (
        <EmptyState title="No matching entries" message="Try clearing your filters." />
      )}

      {!loading && !error && rows && rows.length > 0 && (
        <>
          <section className="card card--flush">
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Entity</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <Fragment key={e.id}>
                      <tr className="data-table__row--expandable" onClick={() => setExpandedId(expandedId === e.id ? null : e.id)}>
                        <td className="cell-muted">{formatDateTime(e.created_at)}</td>
                        <td>{e.actor}</td>
                        <td>
                          <code className="code-chip code-chip--action">{e.action}</code>
                        </td>
                        <td>
                          {e.entity_type}
                          {e.entity_id ? <span className="cell-subtitle"> #{e.entity_id}</span> : null}
                        </td>
                        <td>
                          <span className={`disclosure${expandedId === e.id ? ' disclosure--open' : ''}`} aria-hidden="true">
                            &#9656;
                          </span>
                        </td>
                      </tr>
                      {expandedId === e.id && (
                        <tr className="data-table__expansion-row">
                          <td colSpan={5}>
                            <pre className="diff-view">{JSON.stringify(e.diff, null, 2)}</pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {nextCursor && (
            <div className="pagination">
              <button className="btn btn--ghost btn--sm" disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? 'Loading…' : 'Load older entries'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
